import * as THREE from "three";
import debug from "debug";

const log = debug("app:move");

/**
 * Class for managing object movement in a 3D scene, including drag-and-drop, keyboard controls, and event handling.
 * @category Experience
 * @subcategory Manipulation
 */
class MoveObject {
  /**
   * Constructs the MoveObject instance.
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    this.experience = experience;
    this.sizes = this.experience.sizes;
    this.camera = this.experience.camera;
    this.eventHub = this.experience.eventHub;
    this.vector = new THREE.Vector2();
    this.raycaster = new THREE.Raycaster();
    this.selectedObjects = [];
    this.mouseIsDown = false;
    this.lockedPosition = null;
    this.intersectionPoint = new THREE.Vector3();
    this.selectedGroup = null;
    this.initialY = null;
    this.basePlane = this.experience.world.basePlane.mesh; // Reference BasePlane for raycasting

    this.marqueeIsActive = false;
    this.listenersEnabled = false; // Track if listeners are enabled to prevent duplicates

    // Bind handlers to ensure consistent references
    this.onDragStart = this.onDragStart.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.handleRightClick = this.handleRightClick.bind(this);
    this.handleRightClickEnd = this.handleRightClickEnd.bind(this);
    this.onDrag = this.onDrag.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);

    //optimistic move
    this.originalPosition = new THREE.Vector3();
    this.dragStarted = false;
    this.threshold = 5; // your move threshold in pixels
    this.startMousePosition = { x: 0, y: 0 };

    this.setupEventListeners();
  }

  /**
   * Sets up event listeners for interaction with the scene.
   */
  setupEventListeners() {
    this.eventHub.on("lockEvents", (lock) => {
      lock ? this.disableListeners() : this.enableListeners();
    });
    this.enableListeners();
    
    this.eventHub.on("marqueeStarted", () => {
      this.marqueeIsActive = true;
    });
    this.eventHub.on("modifierMarqueeStarted", () => {
      this.marqueeIsActive = true;
    });
    
  }

  cleanup() {
    log("Cleaning up MoveObject...");

    // Remove all event listeners
    this.disableListeners();
    this.eventHub.off("globalMouseMove", this.onDrag);
    this.eventHub.off("globalMouseUp", this.onDragEnd);
    this.eventHub.off("globalTouchMove", this.onDrag);
    this.eventHub.off("globalTouchEnd", this.onDragEnd);
    this.eventHub.off("lockEvents");

    // Remove group from scene if it exists
    if (this.selectedGroup) {
      this.selectedGroup.traverse((child) => {
        if (child.isMesh) {
          child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.experience.scene.remove(this.selectedGroup);
      this.selectedGroup = null;
    }

    // Clear object references
    this.selectedObjects = [];
    this.lockedPosition = null;
    this.initialY = null;
    this.mouseIsDown = false;
    this.primaryObject = null;

    log("MoveObject cleanup complete.");
  }

  /**
   * Enables event listeners for drag and key interactions.
   */
  enableListeners() {
    // Prevent duplicate registration
    if (this.listenersEnabled) {
      return;
    }
    
    // Disable first to ensure clean state
    this.disableListeners();
    
    this.eventHub.on("singleDragSelect", (selectedObject) => {
      this._lastEventCaptured = this.experience.latestMouseEvent;
      this.onDragStart(selectedObject);
    });
  
    this.eventHub.on("modifierDragSelect", (selectedObject) => {
      this._lastEventCaptured = this.experience.latestMouseEvent;
    
      // Manually update selection here before starting drag
      const selectionManager = this.experience.selectionManager;
      if (selectedObject && !selectionManager.selectedObjects.has(selectedObject)) {
        selectionManager.selectedObjects.add(selectedObject);
        selectionManager.updateSelection(); // emit selectionUpdated
      }
    
      this.onDragStart(selectedObject);
    });
    
  
    this.eventHub.on("globalKeyDown", this.onKeyDown);
    this.eventHub.on("globalMouseDown", this.handleRightClick);
    this.eventHub.on("globalMouseUp", this.handleRightClickEnd);
    
    this.listenersEnabled = true;
  }
  

  /**
   * Disables event listeners for drag and key interactions.
   */
  disableListeners() {
    this.eventHub.off("singleDragSelect", this.onDragStart);
    this.eventHub.off("modifierDragSelect", this.onDragStart);
    this.eventHub.off("globalKeyDown", this.onKeyDown);
    this.eventHub.off("globalMouseDown", this.handleRightClick);
    this.eventHub.off("globalMouseUp", this.handleRightClickEnd);
    
    this.listenersEnabled = false;
  }

  /**
   * Updates pointer coordinates based on the event.
   * @param {MouseEvent|TouchEvent} event - The input event.
   * @returns {Object} - Pointer coordinates.
   */
  updatePointer(event) {
    if (!event) return { clientX: 0, clientY: 0 };
    if (event.touches && event.touches.length > 0) {
      return { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY };
    }
    return { clientX: event.clientX, clientY: event.clientY };
  }
  

  /**
   * Updates the intersection point in the scene based on the current pointer position.
   * @param {MouseEvent|TouchEvent} event - The input event.
   * @returns {Object|null} - The intersection object or null if no intersection.
   */
  updateIntersectionPoint(event) {
    this.pointer = this.updatePointer(event);
    this.vector.x =
      (this.pointer.clientX / this.experience.sizes.reSize.width) * 2 - 1;
    this.vector.y =
      -(this.pointer.clientY / this.experience.sizes.reSize.height) * 2 + 1;

    this.raycaster.setFromCamera(this.vector, this.camera.instance);

    const draggableObjects = Array.from(
      this.experience.selectionManager.selectedObjects
    );
    log("draggable objects", draggableObjects);
    const intersects = this.raycaster.intersectObjects(draggableObjects, true);
    if (intersects.length > 0) {
      return intersects[0];
    }
    return null;
  }

  /**
   * Calculates the locked position for drag movement relative to the intersection point.
   * @param {THREE.Vector3} intersectionPoint - The point of intersection.
   * @param {THREE.Group} group - The selected group of objects.
   */
  calculateLockedPosition(intersectionPoint, group) {
    log("lockedPosition before recalculating", this.lockedPosition);
    log("intersection position before recalculating", intersectionPoint);
    log("primary object position before recalculating", group.position);
    this.lockedPosition = {
      x: group.position.x - intersectionPoint.x,
      z: group.position.z - intersectionPoint.z,
    };
    log("lockedPosition initial", this.lockedPosition);
  }

  /**
   * Handles the start of a drag interaction.
   * @param {Object} selectedObject - The object selected for dragging.
   */
  onDragStart(selectedObject) {
    
    if (!selectedObject) return;
  
    const originalEvent = this._lastEventCaptured;
    if (!originalEvent) return;
  
    this.eventHub.once("selectedObjectsInquiryResponse", (response) => {
      const { selectedObjects } = response || {};
      if (!selectedObjects || !selectedObjects.length) return;

      this.marqueeIsActive = false;
    
      this.selectedObjects = selectedObjects;
    
      this.selectedGroup = new THREE.Group();
      this.selectedObjects.forEach((object) => {
        this.selectedGroup.add(object);
      });
    
      this.experience.scene.add(this.selectedGroup);
      this.initialY = this.selectedGroup.position.y;
    
      // ✅ Call this before anything moves
      this.startMove(this.selectedObjects);
    
      const intersection = this.updateIntersectionPoint(originalEvent);
      if (intersection) {
        this.calculateLockedPosition(intersection.point, this.selectedGroup);
    
        this.movementPlane = new THREE.Plane();
        this.movementPlane.setFromNormalAndCoplanarPoint(
          new THREE.Vector3(0, 1, 0),
          intersection.point
        );
    
        this.dragStarted = true;
        this.originalPosition.copy(this.selectedGroup.position);
        this.startMousePosition = {
          x: originalEvent.clientX,
          y: originalEvent.clientY,
        };
    
        this.initialPosition = {
          x: this.selectedGroup.position.x,
          z: this.selectedGroup.position.z,
        };
      }
    
      this.eventHub.on("globalMouseMove", this.onDrag);
      this.eventHub.on("globalMouseUp", this.onDragEnd);
    });
    
  
    this.eventHub.emit("inquireSelectedObjects");
  }
  
  
  
  
  
  

  /**
   * Handles the end of a drag interaction.
   * @param {MouseEvent|TouchEvent} event - The input event.
   */

  async onDragEnd(event) {
    try {
      if (this.selectedGroup) {
        setTimeout(async () => {
          await this.emitXmdWithPromise(this.selectedGroup);
        }, 10);
      }
    } catch (error) {
      console.error("Error during drag end:", error);
    }
  
    if (this.movementPlane) {
      this.experience.scene.remove(this.movementPlane);
      this.movementPlane = null;
    }
  
    const gridSize = 1;
  
    this.mouseIsDown = false;
    document.body.style.cursor = "default";
  
    this.eventHub.off("globalMouseMove", this.onDrag);
    this.eventHub.off("globalMouseUp", this.onDragEnd);
    this.eventHub.off("globalTouchMove", this.onDrag);
    this.eventHub.off("globalTouchEnd", this.onDragEnd);
  
    if (this.selectedGroup) {
      this.selectedGroup.position.y = this.initialY;

  

            // Snap the entire group first, like before
      this.selectedGroup.position.x =
      Math.round(this.selectedGroup.position.x / gridSize) * gridSize;
      this.selectedGroup.position.z =
      Math.round(this.selectedGroup.position.z / gridSize) * gridSize;

      // ✅ CRITICAL FIX: Update group matrix before detaching
      this.selectedGroup.updateMatrixWorld(true);

      // Then detach and reattach children
      const objects = this.selectedGroup.children.slice();
      objects.forEach((object) => {
      this.experience.scene.attach(object);
      // ✅ CRITICAL FIX: Update each object's matrix after attaching
      object.updateMatrixWorld(true);
      
      // ✅ CRITICAL FIX: Force update of all children's matrices for proper raycasting
      object.traverse((child) => {
        if (child.isMesh || child.isGroup) {
          child.updateMatrixWorld(true);
        }
      });
      });

  
      this.experience.scene.remove(this.selectedGroup);
      this.selectedGroup = null;
    }
  
    this.lockedPosition = null;
    this.initialY = null;
  
    // ✅ This now works correctly because positions were properly applied above
    this.endMove(this.selectedObjects);
  }
  
  
  
  
  
  
  
  

  /**
   * Emits an event with the updated state of the selected group.
   * @param {THREE.Group} selectedGroup - The group to emit.
   * @returns {Promise<void>}
   */

  emitXmdWithPromise(selectedGroup) {
    return new Promise((resolve, reject) => {
      try {
        // Emit xmd to update xmdVault (for the latest scene state)
        this.eventHub.emit("xmd", selectedGroup);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  onDrag(event) {
    if (!this.dragStarted || !this.selectedGroup) return;
  
    const newMousePosition = this.getMouseWorldPosition(event);
    if (newMousePosition) {
      this.selectedGroup.position.x = newMousePosition.x + this.lockedPosition.x;
      this.selectedGroup.position.z = newMousePosition.z + this.lockedPosition.z;
  
      this.eventHub.emit("needsRender", "true");
      this.selectedGroup.updateMatrixWorld(false);
  
      document.body.style.cursor = "move";
    }
  }
  
  onKeyDown(event) {
    const validKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
    if (!validKeys.includes(event.key)) return;
    
    const stepSize = 1;

    this.eventHub.once("selectedObjectsInquiryResponse", (response) => {
      const { selectedObjects } = response || {};
      if (!selectedObjects || !selectedObjects.length) return;

      this.startMove(selectedObjects);

      switch (event.key) {
        case "ArrowUp":
          selectedObjects.forEach((obj) => {
            obj.position.z -= stepSize;
            obj.updateMatrixWorld(true);
          });
          break;
        case "ArrowDown":
          selectedObjects.forEach((obj) => {
            obj.position.z += stepSize;
            obj.updateMatrixWorld(true);
          });
          break;
        case "ArrowLeft":
          selectedObjects.forEach((obj) => {
            obj.position.x -= stepSize;
            obj.updateMatrixWorld(true);
          });
          break;
        case "ArrowRight":
          selectedObjects.forEach((obj) => {
            obj.position.x += stepSize;
            obj.updateMatrixWorld(true);
          });
          break;
      }
      this.eventHub.emit("needsRender", "true");
      this.emitXmdWithPromise(selectedObjects);
      this.endMove(selectedObjects);
    });
    this.eventHub.emit("inquireSelectedObjects");
  }

  handleRightClick(event) {
    if (event.button === 2) {
      document.body.style.cursor = "grabbing";
    }
  }

  handleRightClickEnd(event) {
    if (event.button === 2) {
      document.body.style.cursor = "default";
    }
  }

  startMove(selectedObjects) {
    if (!selectedObjects || !selectedObjects.length) return;
  
    // Clear any previous move tracking to prevent stale data
    if (this.initialWorldPositions) {
      this.initialWorldPositions.clear();
    }
    this.initialWorldPositions = new Map();
    this.objectIds = [];
  
    selectedObjects.forEach((object) => {
      // Ensure matrix is up to date before capturing position
      object.updateMatrixWorld(false);
      const worldPosition = new THREE.Vector3();
      object.getWorldPosition(worldPosition);
      this.initialWorldPositions.set(object, worldPosition.clone());
      this.objectIds.push(object.userData.objectId);
    });
  }
  

  endMove(selectedObjects) {
    if (!selectedObjects || !selectedObjects.length) return;
  
    const delta = { x: 0, z: 0 };
  
    if (this.initialWorldPositions?.has(selectedObjects[0])) {
      const initial = this.initialWorldPositions.get(selectedObjects[0]);
      const final = new THREE.Vector3();
      selectedObjects[0].getWorldPosition(final);
  
      delta.x = final.x - initial.x;
      delta.z = final.z - initial.z;
    }
  
    log("delta", delta);
  
    this.eventHub.emit("logEvent", {
      eventType: "move",
      data: {
        objects: this.objectIds,
        delta: delta,
      },
    });
  }
  
  

  getMouseWorldPosition(event) {
    this.pointer = this.updatePointer(event);
    this.vector.x = (this.pointer.clientX / this.experience.sizes.reSize.width) * 2 - 1;
    this.vector.y = -(this.pointer.clientY / this.experience.sizes.reSize.height) * 2 + 1;
  
    this.raycaster.setFromCamera(this.vector, this.camera.instance);
  
    // 👇 If we're in a marquee, just raycast basePlane like usual
    if (this.marqueeIsActive) {
      const basePlaneHit = this.raycaster.intersectObject(this.basePlane, true);
      return basePlaneHit.length ? basePlaneHit[0].point : null;
    }
  
    // 👇 Else, do the smarter skip-basePlane logic
    // Filter out axes helpers and other helper objects that shouldn't be raycast targets
    const sceneObjects = this.experience.scene.children.filter((obj) => {
      // Exclude axes helpers and other helper objects
      return !(obj instanceof THREE.AxesHelper);
    });
    
    const intersects = this.raycaster.intersectObjects(sceneObjects, true);

    if (!intersects.length) return null;

    let basePlaneHit = null;

    for (let i = 0; i < intersects.length; i++) {
      const obj = intersects[i].object;
      
      // ✅ Skip axes helpers even if they somehow got through
      if (obj instanceof THREE.AxesHelper) {
        continue;
      }
      
      if (obj === this.basePlane) {
        basePlaneHit = intersects[i];
        continue;
      }

      return intersects[i].point; // ✅ Use the first valid non-basePlane hit
    }

    return basePlaneHit?.point || null;
  }
  
  
}

export default MoveObject;
